import { Component, DestroyRef, inject, ChangeDetectorRef, Input } from '@angular/core';
import { commonImports } from '../shared/table-interface';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { combineLatest, forkJoin, map, of, switchMap, take, catchError, Subject, tap, throwError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddVitalsComponent } from '../patient-observation/add-vitals/add-vitals.component';
import { ObsResComponent } from '../obs-res/obs-res.component';
import { PatSympComponent } from "../pat-symp/pat-symp.component";
import { Bundle, BundleEntry, CodeableConcept, Condition, DiagnosticReport, Medication, MedicationRequest, Observation, Quantity, ServiceRequest, AllergyIntolerance, Immunization, Patient, Encounter, Invoice, Procedure } from 'fhir/r4';
import { EncounterServiceService } from '../patient-wrapper/encounter-service.service';
import { MatDividerModule } from '@angular/material/divider';
import { CodeableReferenceDisplayComponent } from "../shared/codeable-reference-display/codeable-reference-display.component";
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { StateService } from '../shared/state.service';
import { backendEndPointToken } from '../app.config';
import { UtilityService } from '../shared/utility.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableDataSource } from '@angular/material/table';
import { MatTableModule } from '@angular/material/table';
import { ChipsDirective } from '../chips.directive';

import { AddObservationComponent } from '../patient-observation/add-observation/add-observation.component';
import { NaPipe } from '../shared/na.pipe';
import { VitalHistoryDialogComponent } from './vital-history-dialog.component';
import { JsonPipe } from '@angular/common';
import { CheckSheetService } from './check-sheet.service';
import { MatButtonModule } from '@angular/material/button';
import { ErrorService } from '../shared/error.service';
import { DiagnosticReportDetailsComponent } from '../diagnostic-report/diagnostic-report-details/diagnostic-report-details.component';
import { EmphasisDirective } from '../shared/directives/emphasis.directive';
import { ResourceStatusChangesService } from '../resource-status-changes/resource-status-changes.service';
import { ReferenceDisplayDirective } from '../shared/reference-display.directive';
import { AuthService } from '../shared/auth/auth.service';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { formMetaData, FormFields, GroupField, SingleCodeField, IndividualField } from '../shared/dynamic-forms.interface2';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ServiceRequestBillingPlaceholderService } from '../shared/services/service-request-billing-placeholder.service';
import { LabBillingPlaceholderService } from '../shared/services/lab-billing-placeholder.service';
import { inferServiceRequestType } from '../shared/service-request-type.util';
import { RequiredObservationResultsTableComponent } from '../shared/required-observation-results-table/required-observation-results-table.component';
import { ProcedureReportDialogComponent } from '../procedure-report/procedure-report-dialog.component';
import { ProcedureReportViewDialogComponent } from '../procedure-report/procedure-report-view-dialog.component';
import { ObservationValueDisplayComponent } from '../shared/observation-value-display/observation-value-display.component';

@Component({
  selector: 'app-check-sheet',
  imports: [...commonImports, NaPipe, JsonPipe,
    MatSelectModule, ChipsDirective,
    ReferenceDisplayDirective,
    EmphasisDirective,
    AddVitalsComponent, ObsResComponent, PatSympComponent, CodeableReferenceDisplayComponent,
    DynamicFormsV2Component,
    MatSnackBarModule,
    ObservationValueDisplayComponent
  ],
  templateUrl: './check-sheet.component.html',
  styleUrls: ['../patients-record/patients-record.component.scss',
    '../encounter-check/encounter-check.component.scss', '../pat-symp/pat-symp.component.scss',
    './check-sheet.component.scss']
  ,
  providers: [CheckSheetService]
})
export class CheckSheetComponent {
  isAuthorizedToView = false;
  hardCodedResolvedData: any
  http = inject(HttpClient);
  resolvedData: any;
  state = inject(StateService);
  utilityService = inject(UtilityService);
  cdr = inject(ChangeDetectorRef);
  destroyRef = inject(DestroyRef);
  errorService = inject(ErrorService);
  authService = inject(AuthService);
  checkSheetService = inject(CheckSheetService);
  dialog = inject(MatDialog);
  dialogRef = inject(MatDialogRef<CheckSheetComponent>, { optional: true });
  dialogData = inject(MAT_DIALOG_DATA, { optional: true });
  backend = inject(backendEndPointToken);
  statusChanges = inject(ResourceStatusChangesService);
  formFieldsDataService = inject(FormFieldsSelectDataService);
  encounterService = inject(EncounterServiceService);
  serviceRequestBillingPlaceholder = inject(ServiceRequestBillingPlaceholderService);
  labBillingService = inject(LabBillingPlaceholderService);
  snackBar = inject(MatSnackBar);

  close() {
    this.dialogRef?.close();
  }

  getPatientHumanId(): string {
    const identifiers = this.resolvedData?.identifier || [];
    const pid = identifiers.find((id: any) => id.system === 'https://elikita-server.daalitech.com/fhir/identifier/pid');
    return pid?.value || this.resolvedData?.id;
  }

  practitionerConditionDataSource: Condition[] = [];
  patientConditionDataSource: Condition[] = [];
  patientId = ""
  labRequestAndValue?: any[];
  medRequestAndValue?: any[];
  // Prefill models for child components

  vitalsPrefill: any = {};
  examPrefill: Array<{ name: string; value: string }> = [];
  symptomsPrefill: Array<any> = [];
  longForm(unit: string | undefined) {
    if (unit === undefined) {
      return ''
    }
    switch (unit) {
      case 'd':
        return 'day';
      case 'h':
        return 'hour';
      case 'min':
        return 'minute';
      case 's':
        return 'second';
      default:
        return unit;
    }
  }
  sourceData: {
    observations: Observation[],
    condition: any[],
    encounter: any[],
    medicationRequests: any[],
    patientSymptoms: any[]
  } = {
      observations: [],
      condition: [],
      encounter: [],
      medicationRequests: [],
      patientSymptoms: []
    };
  @Input() forCheckSheet?: boolean
  @Input() bigSourceData: {
    observations: Observation[],
    condition: any[],
    encounter: any[],
    medicationRequests: any[],
    patientSymptoms: any[]
  } | null = null;
  labRequestFilters = [
    { field: 'encounter.reference', values: [this.state.getCurrentEncounterReference()?.reference] },

    { field: 'subject.reference', values: [this.state.getPatientReference()?.reference] },

  ]
  dataForPatSymp?: Observation[];
  patientSymptomsActivated = false;
  patientSymptomsLoading$ = this.checkSheetService.patientSymptomsLoading$;

  patientInfoActivated = false;
  allergiesLoading$ = this.checkSheetService.allergyIntolerancesLoading$;
  allergyRows: Array<{
    id: string;
    substance: string;
    criticality: string;
    original: AllergyIntolerance;
  }> = [];

  labTestsActivated = false;
  labTestsLoading$ = this.checkSheetService.labRequestsLoading$;
  labFinalReportsLoading$ = this.checkSheetService.finalDiagnosticReportsForEncounterLoading$;

  vitalsActivated = false;
  vitalsLoading$ = this.checkSheetService.vitalsLoading$;

  physicalExamActivated = false;
  physicalExamLoading$ = this.checkSheetService.physicalExamLoading$;

  diagnosisActivated = false;
  diagnosisLoading$ = this.checkSheetService.diagnosisLoading$;

  pastDiagnosisActivated = false;
  pastDiagnosisLoading$ = this.checkSheetService.pastDiagnosisLoading$;
  pastDiagnosisRows: any[] = [];
  pastDiagnosisTableDataSource = new MatTableDataSource<any>();

  pastVaccinationsActivated = false;
  pastVaccinationsLoading$ = this.checkSheetService.immunizationHistoryLoading$;
  immunizationRows: any[] = [];
  immunizationTableDataSource = new MatTableDataSource<any>();


  get canEditEncounter(): boolean {
    const enc: any = this.passedEncounter ?? this.state.currentEncounter.getValue();
    const status = (enc?.status ?? '').toString().toLowerCase();
    const role = (this.authService.user?.getValue?.()?.role ?? this.authService.user?.getValue?.()?.['role'] ?? '').toString().toLowerCase();

    const isEncounterActive = ['planned', 'in-progress'].includes(status);
    const isClinicalStaff = ['doctor', 'remotedoctor', 'healthcareassistant', 'primaryhealthcareprovider'].includes(role);

    return isEncounterActive && isClinicalStaff;
  }

  get isPatientUser(): boolean {
    try {
      const role = this.authService.user?.getValue?.()?.role ?? this.authService.user?.getValue?.()?.['role'];
      return (role ?? '').toString().toLowerCase() === 'patient';
    } catch {
      return false;
    }
  }

  get isRestrictedClinicalRole(): boolean {
    const role = (this.authService.user?.getValue?.()?.role || '').toLowerCase();
    return role === 'healthcareassistant' || role === 'nursemidwife';
  }

  get isMedicationRestrictedRole(): boolean {
    const role = (this.authService.user?.getValue?.()?.role || '').toLowerCase();
    
    if (role === 'healthcareassistant') return true;
    
    if (role === 'alliedhealthprofessional') {
        return !this.authService.canPrescribeMedications();
    }
    
    return false;
  }

  get isAHP(): boolean {
    const role = (this.authService.user?.getValue?.()?.role || '').toLowerCase();
    return role === 'alliedhealthprofessional';
  }

  get canViewLabSection(): boolean {
    if (this.isRestrictedClinicalRole) {
      return this.authService.canAccessServiceRequestCategory('laboratory');
    }
    return true;
  }

  get canAddLabRequest(): boolean {
    return this.canEditEncounter && !this.isPatientUser && (!this.isRestrictedClinicalRole || this.authService.canAccessServiceRequestCategory('laboratory'));
  }

  ngOnInit() {
    if (this.canEditEncounter) {
      this.labRequestDisplayedColumns = Array.from(new Set([...this.labRequestDisplayedColumns, 'revokeAction']))
    }

    if (!this.passedEncounter && this.dialogData?.encounterId) {
      this.http.get<Encounter>(`${this.backend}/Encounter/${this.dialogData.encounterId}`).pipe(
        catchError(err => {
          console.error('Failed to load encounter for check sheet', err);
          this.errorService.openandCloseError('Failed to load encounter details.');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(enc => {
        if (enc) {
          const patientId = enc.subject?.reference?.split('/').pop() || '';
          this.passedEncounter = {
            ...enc,
            patientId: patientId
          } as any;
          this.state.setCurrentEncounter(this.passedEncounter as any);

          // Fetch patient if not already in state to ensure UI updates
          const currentPatientIdInState = this.state.PatientResources.currentPatient.value?.actualResource?.id;
          if (currentPatientIdInState !== patientId) {
            this.http.get<Patient>(`${this.backend}/Patient/${patientId}`).subscribe(pat => {
              if (pat) {
                this.state.processBundleTransaction({
                  resourceType: 'Bundle',
                  type: 'collection',
                  entry: [{ resource: pat }]
                } as any);
              }
            });
          }
        }
        this.initializeStreams();
      });
    } else {
      this.initializeStreams();
    }
  }

  private checkAuthorization() {
    const user = this.authService.user.getValue();
    const userRole = (user?.role || '').toLowerCase();
    
    if (user?.isSuperAdmin || userRole === 'admin' || userRole === 'hospitaladmin') {
      this.isAuthorizedToView = true;
      return;
    }

    const permittedRoles = [
      'doctor', 
      'primaryhealthcareprovider', 
      'remotedoctor', 
      'alliedhealthprofessional', 
      'healthcareassistant', 
      'nursemidwife'
    ];

    const enc: any = this.passedEncounter || this.state.currentEncounter.getValue();
    const encStatus = (enc?.status || '').toLowerCase();
    const isEncounterActive = encStatus === 'in-progress' || encStatus === 'planned';
    
    // Doctors/PHCPs can view ANY historical/completed encounter, regardless of participant status
    const isDoctorRole = ['doctor', 'primaryhealthcareprovider', 'remotedoctor'].includes(userRole);
    if (isDoctorRole && !isEncounterActive) {
      this.isAuthorizedToView = true;
      return;
    }

    if (userRole === 'patient') {
      const loggedInPatientId = user?.fhirResourceId;
      const encounterPatientId = enc?.subject?.reference?.split('/').pop();
      
      if (loggedInPatientId && encounterPatientId === loggedInPatientId) {
        this.isAuthorizedToView = true;
        return;
      }
    } else if (userRole && permittedRoles.includes(userRole)) {
      const loggedInPractitionerId = user?.fhirResourceId;
      const participants = enc?.participant || [];
      const isParticipant = participants.some((p: any) => p.individual?.reference?.includes(loggedInPractitionerId));
      
      if (isParticipant) {
        this.isAuthorizedToView = true;
        return;
      }
    }
    
    this.isAuthorizedToView = false;
  }

  private initializeStreams() {
    this.checkAuthorization();
    if (this.bigSourceData) {
      this.sourceData = this.bigSourceData;
    } else {
      this.state.PatientResources.condition.subscribe((conditions) => {
        this.sourceData.condition = conditions.map(c => c.actualResource);
        this.afterResolvedDataLoaded();
      });

      this.state.PatientResources.observations.subscribe((observations) => {
        this.sourceData.observations = observations.map(o => o.actualResource);
        this.afterResolvedDataLoaded();
      });

      this.state.PatientResources.encounters.subscribe((encounters) => {
        this.sourceData.encounter = encounters.map(e => e.actualResource);
        this.afterResolvedDataLoaded();
      });

      this.state.PatientResources.medicationRequests.subscribe((medRequests) => {
        this.sourceData.medicationRequests = medRequests.map(mr => mr.actualResource);
        this.afterResolvedDataLoaded();
      });

      this.state.PatientResources.currentPatient.subscribe((patient) => {
        this.resolvedData = patient.actualResource;
        this.afterResolvedDataLoaded();
      });
    }
  }
  addPatientReportedSymptoms() {
    if (!this.canEditEncounter) {
      this.errorService.openandCloseError('This encounter is not active. You can no longer add or edit encounter data.');
      return;
    }
    this.dialog.open(PatSympComponent, {
      maxHeight: '90vh',
      maxWidth: '650px',
      autoFocus: false,
      data: {
        forClerkingSheet: true
      }
    })
  }

  onPatientInfoPanelOpened(): void {
    if (this.patientInfoActivated) return;
    this.patientInfoActivated = true;

    const encounterIdToPass = this.passedEncounter ? this.passedEncounter['id'] : this.state.currentEncounter.getValue()?.['id'];
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    this.checkSheetService.connectAllergyIntolerances$(subjectToPass || '', encounterIdToPass || '', null).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((allergies) => {
      this.allergyRows = (allergies || []).map((a) => {
        const substance = a.code?.text || a.code?.coding?.[0]?.display || 'Unknown Allergy';
        const criticality = a.criticality || 'undefined';
        return {
          id: a.id || '',
          substance,
          criticality,
          original: a
        };
      });
      this.cdr.markForCheck();
    });
  }

  onPatientSymptomsPanelOpened(): void {
    if (this.patientSymptomsActivated) return;
    this.patientSymptomsActivated = true;
    const encounterIdToPass = this.passedEncounter ? this.passedEncounter['id'] : this.state.currentEncounter.getValue()?.['id'];
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    // Keep streaming updates until this component is destroyed.
    this.checkSheetService.connectPatientSymptoms$(subjectToPass || "", encounterIdToPass, null).pipe(
      tap((e) => console.log('Patient symptoms stream emitted new value', e)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((list) => {
      const sorted = (list ?? []).slice().sort((a, b) => {
        console.log(a, b);
        // chief complaint on top
        if (a.category && b.category) {
          const aIsChief = a.category.some(cat => cat.coding?.some(coding => coding.code === 'chief-complaint'));
          const bIsChief = b.category.some(cat => cat.coding?.some(coding => coding.code === 'chief-complaint'));
          if (aIsChief && !bIsChief) return -1;
          if (!aIsChief && bIsChief) return 1;
        }
        return 0;
      });

      this.dataForPatSymp = sorted;
      this.cdr.markForCheck();
    });
  }

  onVitalsPanelOpened(): void {
    if (this.vitalsActivated) return;
    this.vitalsActivated = true;

    const encounterIdToPass = this.passedEncounter ? this.passedEncounter['id'] : this.state.currentEncounter.getValue()?.['id'];
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    this.checkSheetService.connectPatientVitals$(subjectToPass || "", encounterIdToPass, null).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((vitalObservations) => {
      this.rebuildVitalsFromVitalObservations(vitalObservations || []);
    });
  }

  onPhysicalExamPanelOpened(): void {
    if (this.physicalExamActivated) return;
    this.physicalExamActivated = true;

    const encounterIdToPass = this.passedEncounter ? this.passedEncounter['id'] : this.state.currentEncounter.getValue()?.['id'];
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    this.checkSheetService.connectPhysicalExam$(subjectToPass || "", encounterIdToPass, null).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((examObservations) => {
      this.rebuildPhysicalExamFromExamObservations(examObservations || []);
    });
  }

  // ----- LAB TEST REQUESTS (ServiceRequest) -----
  labRequestDisplayedColumns: string[] = ['status', 'code', 'request', 'action',];
  labRequestTableDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  labRequestRows: Array<{
    id: string;
    status: string;
    code: string;
    authoredOn: string;
    hasFinalReport: boolean;
    reportAvailabilityStatus: 'available' | 'pending' | 'unavailable';
    original: ServiceRequest;
  }> = [];

  labReportAvailabilityLabel(status: 'available' | 'pending' | 'unavailable'): string {
    switch (status) {
      case 'available':
        return 'Available';
      case 'pending':
        return 'Checking…';
      case 'unavailable':
      default:
        return 'Not available';
    }
  }

  onLabTestsPanelOpened(): void {
    if (this.labTestsActivated) return;
    this.labTestsActivated = true;

    const encounterIdToPass = this.passedEncounter ? this.passedEncounter['id'] : this.state.currentEncounter.getValue()?.['id'];
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    const labRequests$ = this.checkSheetService.connectLabServiceRequests$(subjectToPass || '', encounterIdToPass || '', null);
    const finalReportsForEncounter$ = this.checkSheetService.connectFinalDiagnosticReportsForEncounter$(subjectToPass || '', encounterIdToPass || '', null);

    combineLatest([labRequests$, finalReportsForEncounter$, this.labFinalReportsLoading$]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(([requests, reports, finalReportsLoading]) => {
      if(reports) {
// $#$      // /* alert(JSON.stringify({ reports, finalReportsLoading }) */);
      }
      const list = (requests ?? []).slice();
      const rows = list.map((sr) => {
        const code = this.utilityService.chooseFirstStringFromCodeableConcept((sr as any)?.code);
        const authoredOn = (sr as any)?.authoredOn || '';
        const status = (sr as any)?.status || 'unknown';

        const srId = (sr as any)?.id || '';
        const hasFinalReport = !!srId && (reports ?? []).some((dr) => {
// $#$          // /* alert('Checking if report '+ dr.basedOn?.[0]?.reference + ' is based on service request '+ srId); */
          const basedOn = (dr?.basedOn ?? []) as any[];
          return basedOn.some((r) => (r?.reference ?? '').toString().includes(`ServiceRequest/${srId}`));
        });

        const reportAvailabilityStatus: 'available' | 'pending' | 'unavailable' =
          hasFinalReport ? 'available' : (finalReportsLoading ? 'pending' : 'unavailable');

        return {
          id: srId,
          status,
          code: code || 'Lab Request',
          authoredOn,
          hasFinalReport,
          reportAvailabilityStatus,
          original: sr
        };
      });

      this.labRequestRows = rows;
      this.labRequestTableDataSource.data = this.labRequestRows.slice();
      this.cdr.markForCheck();
    });
  }

  viewFinalDiagnosticReportForServiceRequest(row: { original: ServiceRequest }): void {
    const srId = (row?.original as any)?.id;
    if (!srId) {
      this.errorService.openandCloseError('This lab request has no id, so its report cannot be resolved.');
      return;
    }

    const encounterIdToPass = this.passedEncounter ? this.passedEncounter['id'] : this.state.currentEncounter.getValue()?.['id'];
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    this.checkSheetService.findFinalDiagnosticReportForServiceRequest$(subjectToPass || '', encounterIdToPass || '', srId).pipe(
      take(1)
    ).subscribe((dr) => {
      if (!dr) {
        this.errorService.openandCloseError('No FINAL DiagnosticReport found for this lab request.');
        return;
      }

      this.dialog.open(DiagnosticReportDetailsComponent, {
        maxWidth: '980px',
        maxHeight: '90vh',
        autoFocus: false,
        data: {
          diagnosticReport: dr
        }
      });
    });
  }

  viewFinalDiagnosticReportForLabRequest(row: { original: ServiceRequest }): void {
    this.viewFinalDiagnosticReportForServiceRequest(row);
  }

  // ----- MEDICATIONS (MedicationRequest) -----
  medicationsActivated = false;
  medicationsLoading$ = this.checkSheetService.medicationRequestsLoading$;

  onMedicationsPanelOpened(): void {
    if (this.medicationsActivated) return;
    this.medicationsActivated = true;

    const encounterIdToPass = this.passedEncounter ? this.passedEncounter['id'] : this.state.currentEncounter.getValue()?.['id'];
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    this.checkSheetService.connectMedicationRequests$(subjectToPass || '', encounterIdToPass || '', null).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((requests) => {
      this.medRequestAndValue = (requests || []).map((m: any) => {
        const name = m.medicationCodeableConcept?.text || m.medicationCodeableConcept?.coding?.[0]?.display || 'Unknown Medication';
        const value = m.dosageInstruction?.[0]?.text || 'No dosage info';

        // Compute timing safely to avoid rendering "undefinedx per undefined"
        let timing = '';
        try {
          timing = m.dosageInstruction?.[0]?.timing?.code?.text || '';
          if (!timing) {
            const rep = m.dosageInstruction?.[0]?.timing?.repeat;
            if (rep) {
              const freq = rep.frequency ?? rep.frequencyMax ?? null;
              const periodUnit = rep.periodUnit ?? rep.period ?? null;
              if (freq != null && periodUnit) {
                const unitStr = this.longForm(periodUnit) || periodUnit;
                timing = `${freq}x per ${unitStr}`;
              } else {
                timing = '';
              }
            }
          }
        } catch {
          timing = '';
        }

        return { name, value, timing, dosage: m.dosageInstruction, original: m };
      });
      this.cdr.markForCheck();
    });
  }

  addMedicationRequest(): void {
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();
    this.encounterService.addMedicationRequest(subjectToPass || '');
  }

  // ----- DIAGNOSIS (Condition) -----
  diagnosisDisplayedColumns: string[] = ['status', 'verificationStatus', 'diagnosis', 'severity', 'recorded', 'action'];
  diagnosisTableDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  diagnosisRows: Array<{
    id: string;
    status: string;
    verificationStatus: string;
    diagnosis: string;
    severity: string;
    recordedDate: string;
    original: Condition;
  }> = [];

  onDiagnosisPanelOpened(): void {
    if (this.diagnosisActivated) return;
    this.diagnosisActivated = true;

    const encounterIdToPass = this.passedEncounter ? this.passedEncounter['id'] : this.state.currentEncounter.getValue()?.['id'];
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    this.checkSheetService.connectDiagnosisConditions$(subjectToPass || '', encounterIdToPass || '', null).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((conditions) => {
      const list = (conditions ?? []).slice();
      const getWhen = (c: any): number => new Date(c?.recordedDate || c?.onsetDateTime || c?.meta?.lastUpdated || 0).getTime();
      list.sort((a: any, b: any) => getWhen(b) - getWhen(a));

      const rows = list.map((c: any) => {
        const diagnosis = this.utilityService.chooseFirstStringFromCodeableConcept(c?.code) || c?.code?.text || c?.code?.coding?.[0]?.display || c?.code?.coding?.[0]?.code || 'Diagnosis';
        const severity = this.utilityService.chooseFirstStringFromCodeableConcept(c?.severity) || c?.severity?.text || c?.severity?.coding?.[0]?.display || '';
        const status = c?.clinicalStatus?.coding?.[0]?.code || c?.clinicalStatus?.text || 'unknown';
        const verificationStatus = c?.verificationStatus?.coding?.[0]?.code || c?.verificationStatus?.text || 'unknown';
        const recordedDate = c?.recordedDate || c?.onsetDateTime || '';

        return {
          id: c?.id || '',
          status,
          verificationStatus,
          diagnosis,
          severity,
          recordedDate,
          original: c as Condition
        };
      });

      this.diagnosisRows = rows;
      this.diagnosisTableDataSource.data = this.diagnosisRows.slice();
      this.cdr.markForCheck();
    });
  }

  orderAnotherLabTest(): void {
    if (!this.canEditEncounter) {
      this.errorService.openandCloseError('This encounter is not active. You can no longer order lab tests for it.');
      return;
    }
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();
    this.encounterService.addServiceRequest(subjectToPass || null, 'lab', false, true);
  }

  revokeServiceRequest(row: { original: ServiceRequest } | ServiceRequest): void {
    const sr: any = (row as any)?.original ? (row as any).original : row;
    const srId = (sr as any)?.id;
    if (!srId) {
      this.errorService.openandCloseError('This request has no id, so it cannot be revoked.');
      return;
    }
    if (!this.canRevokeServiceRequest(sr)) {
      this.errorService.openandCloseError('Only active or on-hold requests can be revoked. Completed requests must be corrected through an audited reversal workflow.');
      return;
    }
    if (!this.canEditEncounter) {
      this.errorService.openandCloseError('This encounter is not active. You can no longer revoke requests for it.');
      return;
    }
    const patientId = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();
    if (!patientId) {
      this.errorService.openandCloseError('Patient context is missing. Cannot revoke this request.');
      return;
    }

    const currentPatientId = this.state.currentPatientId$.getValue() || '';
    const persistMode: 'auto' | 'patient' | 'org' = currentPatientId && currentPatientId === patientId ? 'patient' : 'org';

    this.resolveBillingSnapshotForRequest(sr as ServiceRequest, patientId).pipe(
      take(1),
      switchMap(({ invoices, hasBalancedInvoice }) => {
        const fallbackInvoices: Invoice[] = Array.isArray((sr as any)?.relatedInvoices)
          ? (((sr as any).relatedInvoices as any[]).filter(Boolean) as Invoice[])
          : [];
        const invoicesToUse = (invoices?.length ? invoices : fallbackInvoices) ?? [];
        const srUpdate$ = this.statusChanges.changeResourceStatus('ServiceRequest', srId, 'revoked', persistMode).pipe(
          catchError((error) => {
            console.error('Failed to revoke ServiceRequest', srId, error);
            this.errorService.openandCloseError('Failed to revoke this request.');
            return throwError(() => error);
          })
        );

        if (hasBalancedInvoice) {
          return srUpdate$.pipe(map(() => ({ cancelledInvoices: 0, paid: true })));
        }

        const idsToCancel = (invoicesToUse || [])
          .map((inv: Invoice) => ({
            id: (inv?.id ?? '').toString().trim(),
            status: (inv?.status ?? '').toString().toLowerCase()
          }))
          .filter((inv) => inv.id && !['balanced', 'cancelled', 'entered-in-error'].includes(inv.status))
          .map((inv) => inv.id as string);

        if (!idsToCancel.length) {
          return srUpdate$.pipe(map(() => ({ cancelledInvoices: 0, paid: false })));
        }

        const invUpdates$ = forkJoin(
          idsToCancel.map((invoiceId) =>
            this.statusChanges.changeResourceStatus('Invoice', invoiceId, 'cancelled', persistMode).pipe(
              catchError((error) => {
                console.error('Failed to cancel Invoice', invoiceId, error);
                return of(null);
              })
            )
          )
        );

        return forkJoin({ sr: srUpdate$, inv: invUpdates$ }).pipe(
          map(() => ({ cancelledInvoices: idsToCancel.length, paid: false }))
        );
      })
    ).subscribe({
      next: ({ cancelledInvoices, paid }) => {
        if (paid) {
          this.snackBar.open('Request revoked. Invoice already balanced, no cancellation needed.', 'Dismiss', { duration: 4500 });
          return;
        }
        if (cancelledInvoices > 0) {
          this.snackBar.open(`Request revoked. Cancelled ${cancelledInvoices} unpaid invoice(s).`, 'Dismiss', { duration: 4500 });
          return;
        }
        this.snackBar.open('Request revoked. No unpaid invoices were linked to this request.', 'Dismiss', { duration: 4500 });
      },
      error: () => {
        // error already surfaced via catchError; nothing else to do here
      }
    });
  }

  canRevokeServiceRequest(row: { original?: ServiceRequest; status?: string } | ServiceRequest): boolean {
    const sr: any = (row as any)?.original ? (row as any).original : row;
    const status = (sr?.status ?? '').toString().trim().toLowerCase();
    return status === 'active' || status === 'on-hold';
  }

  private resolveBillingSnapshotForRequest(
    sr: ServiceRequest,
    patientId: string
  ) {
    const type = inferServiceRequestType(sr);
    if (type === 'laboratory') {
      return this.labBillingService.resolveBillingAndPlaceholderForLab(sr, patientId).pipe(
        map(({ hasBalancedInvoice, linkedInvoices }) => ({ hasBalancedInvoice: !!hasBalancedInvoice, invoices: linkedInvoices ?? [] }))
      );
    }
    if (type === 'imaging') {
      return this.labBillingService.resolveBillingAndPlaceholderForImagingStudy(sr, patientId).pipe(
        map(({ hasBalancedInvoice, linkedInvoices }) => ({ hasBalancedInvoice: !!hasBalancedInvoice, invoices: linkedInvoices ?? [] }))
      );
    }
    if (type === 'procedure' || type === 'surgery') {
      return this.labBillingService.resolveBillingAndPlaceholderForProcedure(sr, patientId).pipe(
        map(({ hasBalancedInvoice, linkedInvoices }) => ({ hasBalancedInvoice: !!hasBalancedInvoice, invoices: linkedInvoices ?? [] }))
      );
    }
    return this.serviceRequestBillingPlaceholder.resolvePaymentForServiceRequest(sr, patientId).pipe(
      map(({ hasBalancedInvoice, invoices }) => ({ hasBalancedInvoice: !!hasBalancedInvoice, invoices: invoices ?? [] }))
    );
  }

  // ----- OTHER SERVICE REQUESTS (non-lab ServiceRequest) -----
  otherServiceRequestsActivated = false;
  otherServiceRequestDisplayedColumns: string[] = ['status', 'code', 'request', 'report', 'action'];
  otherServiceRequestTableDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  otherServiceRequestRows: Array<{
    id: string;
    status: string;
    code: string;
    authoredOn: string;
    hasFinalReport: boolean;
    reportAvailabilityStatus: 'available' | 'pending' | 'unavailable';
    evidenceLabel: string;
    evidenceActionLabel: string;
    evidenceAvailable: boolean;
    missingEvidenceText: string;
    serviceRequestType: string;
    procedure?: Procedure | null;
    original: ServiceRequest;
  }> = [];
  otherServiceRequestsLoading$ = this.checkSheetService.finalDiagnosticReportsForEncounterLoading$;

  onOtherServiceRequestsPanelOpened(): void {
    if (this.otherServiceRequestsActivated) return;
    this.otherServiceRequestsActivated = true;

    const encounterIdToPass = this.passedEncounter ? this.passedEncounter['id'] : this.state.currentEncounter.getValue()?.['id'];
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    const otherServiceRequests$ = this.checkSheetService.connectOtherServiceRequests$(subjectToPass || '', encounterIdToPass || '', null);
    const finalReportsForEncounter$ = this.checkSheetService.connectFinalDiagnosticReportsForEncounter$(subjectToPass || '', encounterIdToPass || '', null);

    combineLatest([otherServiceRequests$, finalReportsForEncounter$, this.checkSheetService.finalDiagnosticReportsForEncounterLoading$]).pipe(
      switchMap(([requests, reports, finalReportsLoading]) => {
      let list = (requests ?? []).slice();
      const userRole = (this.authService.user?.getValue?.()?.role || '').toLowerCase();
      
      if (this.isAHP) {
        const allowedCategories = this.authService.getAllowedServiceRequestCategories().map((c: string) => c.toLowerCase().trim());
        list = list.filter((s: any) => {
          const cat = (s.category?.[0]?.coding?.[0]?.code || s.category?.[0]?.text || s.category?.[0]?.coding?.[0]?.display || '').toLowerCase().trim();
          return allowedCategories.includes(cat);
        });
      } else if (userRole === 'healthcareassistant' || userRole === 'nursemidwife') {
        list = list.filter((s: any) => {
          const cat = (s.category?.[0]?.coding?.[0]?.code || s.category?.[0]?.text || s.category?.[0]?.coding?.[0]?.display || '').toLowerCase().trim();
          return cat !== 'laboratory' && cat !== 'imaging' && cat !== 'lab';
        });
      }
      
        if (!list.length) return of([] as any[]);

        return forkJoin(list.map((sr) => this.resolveOtherServiceRequestEvidence$(sr, subjectToPass || '', reports, finalReportsLoading)));
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((rows) => {
      this.otherServiceRequestRows = rows;
      this.otherServiceRequestTableDataSource.data = this.otherServiceRequestRows.slice();
      this.cdr.markForCheck();
    });
  }

  private resolveOtherServiceRequestEvidence$(
    sr: ServiceRequest,
    patientId: string,
    reports: DiagnosticReport[],
    finalReportsLoading: boolean
  ) {
        const code = this.utilityService.chooseFirstStringFromCodeableConcept((sr as any)?.code);
        const authoredOn = (sr as any)?.authoredOn || '';
        const status = (sr as any)?.status || 'unknown';
        const srId = (sr as any)?.id || '';
    const type = inferServiceRequestType(sr);

    if (type === 'procedure' || type === 'surgery') {
      return this.listProceduresForServiceRequest$(sr, patientId).pipe(
        map((procedures) => {
          const completedProcedures = procedures.filter((p: any) => (p?.status ?? '').toString().toLowerCase() === 'completed');
          // A finalized Procedure is the report of record; do not let a newer
          // draft cause the finalized report to be hidden.
          const selectedProcedure = this.pickLatestProcedure(completedProcedures)
            || this.pickLatestProcedure(procedures);
          const evidenceAvailable = completedProcedures.length > 0;
          return {
            id: srId,
            status,
            code: code || 'Service Request',
            authoredOn,
            hasFinalReport: evidenceAvailable,
            reportAvailabilityStatus: evidenceAvailable ? 'available' : 'unavailable',
            evidenceLabel: type === 'surgery' ? 'Surgery Procedure' : 'Procedure',
            evidenceActionLabel: selectedProcedure ? 'View Procedure' : 'Add Procedure',
            evidenceAvailable,
            missingEvidenceText: evidenceAvailable ? '' : (selectedProcedure ? 'Procedure draft exists but is not final.' : 'No Procedure found.'),
            serviceRequestType: type,
            procedure: selectedProcedure,
            original: sr
          };
        })
      );
    }

    if (type === 'observation') {
      return this.resolveObservationRequestReadiness$(sr, patientId).pipe(
        map((readiness) => ({
          id: srId,
          status,
          code: code || 'Service Request',
          authoredOn,
          hasFinalReport: readiness.complete,
          reportAvailabilityStatus: readiness.complete ? 'available' : 'unavailable',
          evidenceLabel: 'Observation Results',
          evidenceActionLabel: 'Results',
          evidenceAvailable: readiness.complete,
          missingEvidenceText: readiness.missingDisplays.length ? `Missing: ${readiness.missingDisplays.join(', ')}` : 'No final Observation result found.',
          serviceRequestType: type,
          original: sr
        }))
      );
    }

        const hasFinalReport = !!srId && (reports ?? []).some((dr) => {
          const basedOn = (dr?.basedOn ?? []) as any[];
          return basedOn.some((ref) => (ref?.reference ?? '').toString().includes(`ServiceRequest/${srId}`));
        });
        const reportAvailabilityStatus: 'available' | 'pending' | 'unavailable' =
          hasFinalReport ? 'available' : (finalReportsLoading ? 'pending' : 'unavailable');

        return of({
          id: srId,
          status,
          code: code || 'Service Request',
          authoredOn,
          hasFinalReport,
          reportAvailabilityStatus,
      evidenceLabel: 'Report',
      evidenceActionLabel: 'Report',
      evidenceAvailable: hasFinalReport,
      missingEvidenceText: hasFinalReport ? '' : 'No final DiagnosticReport found.',
      serviceRequestType: type,
          original: sr
        });
  }

  viewServiceRequestEvidence(row: any): void {
    const type = (row?.serviceRequestType ?? inferServiceRequestType(row?.original)).toString();
    if (type === 'observation') {
      this.viewObservationResultsForServiceRequest(row?.original as ServiceRequest);
      return;
    }
    if (type === 'procedure' || type === 'surgery') {
      this.openProcedureReportForServiceRequest(row);
      return;
    }
    this.viewFinalDiagnosticReportForServiceRequest(row);
  }

  private openProcedureReportForServiceRequest(row: any): void {
    const sr = row?.original as ServiceRequest;
    const patientId = sr?.subject?.reference?.split('/').pop() || this.state.currentPatientId$.getValue() || '';
    if (!sr?.id || !patientId) {
      this.errorService.openandCloseError('Patient or ServiceRequest context is missing.');
      return;
    }

    if (!row?.procedure && (!this.canEditEncounter || this.isPatientUser)) {
      this.errorService.openandCloseError(row?.missingEvidenceText || 'No Procedure found for this request.');
      return;
    }

    const isFinalized = (row?.procedure?.status ?? '').toString().toLowerCase() === 'completed';
    const dialogData = {
      serviceRequest: sr,
      patientId,
      existingProcedure: row?.procedure ?? null,
      readOnly: isFinalized || !this.canEditEncounter || this.isPatientUser
    };

    if (!isFinalized && this.canEditEncounter && !this.isPatientUser) {
      this.dialog.open(ProcedureReportDialogComponent, {
        maxWidth: '760px',
        maxHeight: '92vh',
        autoFocus: false,
        data: dialogData
      }).afterClosed().pipe(take(1)).subscribe((saved) => {
        if (!saved?.id) return;
        this.otherServiceRequestsActivated = false;
        this.onOtherServiceRequestsPanelOpened();
      });
      return;
    }

    this.dialog.open(ProcedureReportViewDialogComponent, {
      maxWidth: '760px',
      maxHeight: '92vh',
      autoFocus: false,
      data: dialogData
    }).afterClosed().pipe(take(1)).subscribe((saved) => {
      if (!saved?.id) return;
      this.otherServiceRequestsActivated = false;
      this.onOtherServiceRequestsPanelOpened();
    });
  }

  private viewObservationResultsForServiceRequest(sr: ServiceRequest): void {
    const patientId = sr?.subject?.reference?.split('/').pop() || this.state.currentPatientId$.getValue() || '';
    if (!sr?.id || !patientId) {
      this.errorService.openandCloseError('Patient or ServiceRequest context is missing.');
      return;
    }

    this.dialog.open(RequiredObservationResultsTableComponent, {
      maxWidth: '900px',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        serviceRequest: sr,
        patientId,
        forViewingOnly: true,
        enforcePaymentForFinal: false,
        paymentBalanced: true
      }
    });
  }

  private listProceduresForServiceRequest$(sr: ServiceRequest, patientId?: string) {
    const srId = (sr?.id ?? '').toString().trim();
    if (!srId) return of([] as Procedure[]);
    const srRef = `ServiceRequest/${srId}`;
    return this.http.get<Bundle<Procedure>>(
      `${this.backend}/Procedure?based-on=${encodeURIComponent(srRef)}${patientId ? `&subject=${encodeURIComponent(`Patient/${patientId}`)}` : ''}&_count=100`
    ).pipe(
      map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as Procedure[]),
      map((procedures) => (procedures || []).filter((p: any) => {
        const refs = Array.isArray(p?.basedOn) ? p.basedOn : [];
        return refs.some((r: any) => (r?.reference ?? '').toString().includes(`ServiceRequest/${srId}`));
      })),
      catchError(() => of([] as Procedure[]))
    );
  }

  private pickLatestProcedure(procedures: Procedure[]): Procedure | null {
    const list = (procedures ?? []).slice();
    if (!list.length) return null;
    const getWhen = (p: any): number => new Date(p?.performedDateTime || p?.meta?.lastUpdated || 0).getTime();
    list.sort((a, b) => getWhen(b) - getWhen(a));
    return list[0] ?? null;
  }

  private resolveObservationRequestReadiness$(sr: ServiceRequest, patientId?: string) {
    const srId = (sr?.id ?? '').toString().trim();
    if (!srId) return of({ complete: false, missingDisplays: ['ServiceRequest id'] });
    const srCode = this.getServiceRequestCode(sr);
    const requiredCodes$ = srCode
      ? this.encounterService.getConfiguredRequiredObservationCodes$(srCode).pipe(catchError(() => of([] as any[])))
      : of([] as any[]);

    return combineLatest({
      requiredCodes: requiredCodes$,
      observations: this.listFinalObservationsForServiceRequest$(sr, patientId)
    }).pipe(
      map(({ requiredCodes, observations }) => {
        const finals = (observations || []).filter((o: any) => (o?.status ?? '').toString().toLowerCase() === 'final');
        const configured = Array.isArray(requiredCodes) ? requiredCodes : [];
        if (!configured.length) {
          return {
            complete: finals.length > 0,
            missingDisplays: finals.length > 0 ? [] : ['at least one final observation result']
          };
        }

        const missing = configured.filter((cc: any) => !finals.some((o: any) => this.codesMatch(o?.code, cc)));
        return {
          complete: missing.length === 0,
          missingDisplays: missing.map((cc: any) => this.displayCodeableConcept(cc))
        };
      }),
      take(1)
    );
  }

  private listFinalObservationsForServiceRequest$(sr: ServiceRequest, patientId?: string) {
    const srId = (sr?.id ?? '').toString().trim();
    if (!srId) return of([] as Observation[]);
    const srRef = `ServiceRequest/${srId}`;
    return this.http.get<Bundle<Observation>>(
      `${this.backend}/Observation?based-on=${encodeURIComponent(srRef)}${patientId ? `&subject=${encodeURIComponent(`Patient/${patientId}`)}` : ''}&_count=200`
    ).pipe(
      map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as Observation[]),
      map((list) => (list || []).filter((o: any) => {
        const refs = Array.isArray(o?.basedOn) ? o.basedOn : [];
        return refs.some((r: any) => (r?.reference ?? '').toString().includes(`ServiceRequest/${srId}`));
      })),
      catchError(() => of([] as Observation[]))
    );
  }

  private getServiceRequestCode(sr: ServiceRequest): string | null {
    const codings = Array.isArray(sr?.code?.coding) ? sr.code.coding : [];
    const c = codings[0];
    if (c?.code) return String(c.code);
    return null;
  }

  private displayCodeableConcept(cc: any): string {
    return (cc?.text || cc?.coding?.[0]?.display || cc?.coding?.[0]?.code || 'Unnamed observation').toString();
  }

  private normalizeCodeTokens(cc: any): Set<string> {
    const tokens = new Set<string>();
    const coding = Array.isArray(cc?.coding) ? cc.coding : [];
    for (const c of coding) {
      const code = (c?.code || '').toString().trim();
      if (!code) continue;
      const system = (c?.system || '').toString().trim();
      tokens.add(`${system}|${code}`);
      tokens.add(`|${code}`);
    }
    const text = (cc?.text || '').toString().trim().toLowerCase();
    if (text) tokens.add(`text|${text}`);
    return tokens;
  }

  private codesMatch(a: any, b: any): boolean {
    if (!a || !b) return false;
    const at = this.normalizeCodeTokens(a);
    const bt = this.normalizeCodeTokens(b);
    for (const t of at) {
      if (bt.has(t)) return true;
    }
    return false;
  }

  requestAnotherService(): void {
    if (!this.canEditEncounter) {
      this.errorService.openandCloseError('This encounter is not active. You can no longer request services for it.');
      return;
    }
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();
    // null type => "General" category in the form.
    this.encounterService.addServiceRequest(subjectToPass || null, null, false, true);
  }


  // ----- PAST DIAGNOSIS (Condition History) -----
  onPastDiagnosisPanelOpened(): void {
    if (this.pastDiagnosisActivated) return;
    this.pastDiagnosisActivated = true;

    const encounterIdToPass = this.passedEncounter ? this.passedEncounter['id'] : this.state.currentEncounter.getValue()?.['id'];
    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    this.checkSheetService.connectPastDiagnosisConditions$(subjectToPass || '', encounterIdToPass || '', null).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((conditions) => {
      this.rebuildPastDiagnosisFromConditions(conditions || []);
    });
  }

  private rebuildPastDiagnosisFromConditions(conditions: Condition[]): void {
    this.pastDiagnosisRows = (conditions ?? []).map(c => {
      const diagnosis = this.utilityService.chooseFirstStringFromCodeableConcept(c?.code) || c?.code?.text || c?.code?.coding?.[0]?.display || 'Diagnosis';
      const status = c?.clinicalStatus?.coding?.[0]?.code || c?.clinicalStatus?.text || 'unknown';
      const verificationStatus = c?.verificationStatus?.coding?.[0]?.code || c?.verificationStatus?.text || 'unknown';
      const recordedDate = (c as any)?.recordedDate || c?.onsetDateTime || '';
      return {
        id: c?.id || '',
        status,
        verificationStatus,
        diagnosis,
        recordedDate,
        original: c
      };
    });
    this.pastDiagnosisTableDataSource.data = this.pastDiagnosisRows;
    this.cdr.markForCheck();
  }

  openChangeDiagnosisStatus(diagnosis: any): void {
    const cond = diagnosis.original || diagnosis;
    this.openChangeConditionStatusesDialog(cond);
  }

  private openChangeConditionStatusesDialog(condition: any): void {
    const conditionId = (condition?.id ?? '').toString().trim();
    if (!conditionId) {
      this.errorService.openandCloseError('Cannot change status: Condition has no id.');
      return;
    }

    if (!this.authService.can('condition', 'update')) {
      this.errorService.openandCloseError('You do not have permission to edit diagnoses.');
      return;
    }

    const currentClinical = (condition?.clinicalStatus?.coding?.[0]?.code ?? condition?.clinicalStatus?.text ?? '').toString().trim().toLowerCase();
    const currentVerification = (condition?.verificationStatus?.coding?.[0]?.code ?? condition?.verificationStatus?.text ?? '').toString().trim().toLowerCase();

    const clinicalOptions$ = this.formFieldsDataService.getFormFieldSelectData('condition', 'clinicalStatus').pipe(
      catchError(() => of([] as any[]))
    );
    const verificationOptions$ = this.formFieldsDataService.getFormFieldSelectData('condition', 'verificationStatus').pipe(
      catchError(() => of([] as any[]))
    );

    forkJoin({ clinicalOptions: clinicalOptions$, verificationOptions: verificationOptions$ }).pipe(take(1)).subscribe(({ clinicalOptions, verificationOptions }) => {
      const clinicalCodes = this.toUniqueCodes(clinicalOptions);
      const verificationCodes = this.toUniqueCodes(verificationOptions);

      const fallbackClinical = ['active', 'recurrence', 'inactive', 'remission', 'resolved'];
      const fallbackVerification = ['unconfirmed', 'provisional', 'differential', 'confirmed', 'refuted', 'entered-in-error'];

      const clinicalList = clinicalCodes.length ? clinicalCodes : fallbackClinical;
      const verificationList = verificationCodes.length ? verificationCodes : fallbackVerification;

      const dynF = this.dialog.open(DynamicFormsV2Component, {
        width: '520px',
        data: {
          formMetaData: <formMetaData>{
            formName: 'Change Diagnosis Status',
            closeDialogOnSubmit: true,
            formDescription: 'Update clinical and verification status. If clinical status becomes resolved, abatement date/time is required.',
            submitText: 'Update'
          },
          formFields: <FormFields[]>[
            <GroupField>{
              generalProperties: <any>{
                fieldApiName: 'statusGroup',
                fieldName: 'Status',
                fieldLabel: 'Status',
                fieldType: 'IndividualField',
                isArray: false,
                isGroup: true
              },
              keys: ['clinicalStatus', 'verificationStatus', 'abatementDateTime'],
              groupFields: {
                clinicalStatus: <SingleCodeField>{
                  generalProperties: <any>{
                    fieldApiName: 'clinicalStatus',
                    fieldLabel: 'Clinical Status',
                    fieldName: 'Clinical Status',
                    fieldType: 'SingleCodeField',
                    inputType: 'select',
                    isArray: false,
                    isGroup: false,
                    value: currentClinical || (clinicalList[0] ?? ''),
                    validations: [{ type: 'default', isFunction: false, name: 'required' }],
                    controllingField: [
                      {
                        isAControlField: true,
                        dependentFieldVisibilityTriggerValue: 'resolved',
                        controlledFieldDependencyId: 'abatementDateTime'
                      }
                    ]
                  },
                  data: clinicalList,
                  dataType: 'string'
                },
                verificationStatus: <SingleCodeField>{
                  generalProperties: <any>{
                    fieldApiName: 'verificationStatus',
                    fieldLabel: 'Verification Status',
                    fieldName: 'Verification Status',
                    fieldType: 'SingleCodeField',
                    inputType: 'select',
                    isArray: false,
                    isGroup: false,
                    value: currentVerification || (verificationList[0] ?? ''),
                    validations: [{ type: 'default', isFunction: false, name: 'required' }]
                  },
                  data: verificationList,
                  dataType: 'string'
                },
                abatementDateTime: <IndividualField>{
                  generalProperties: <any>{
                    fieldApiName: 'abatementDateTime',
                    fieldLabel: 'Abatement Date/Time',
                    fieldName: 'Abatement Date/Time',
                    fieldType: 'IndividualField',
                    inputType: 'datetime-local',
                    isArray: false,
                    isGroup: false,
                    value: '',
                    dependence_id: 'abatementDateTime'
                  },
                  data: null,
                  dataType: 'string'
                }
              }
            }
          ]
        }
      });

      dynF.afterClosed().pipe(
        take(1),
        switchMap((formResult) => {
          if (!formResult) return of(null);
          const valuesRoot = (formResult?.values ?? {}) as any;
          const values = (valuesRoot?.statusGroup ?? valuesRoot) as any;
          const nextClinical = (values?.clinicalStatus ?? '').toString().trim().toLowerCase();
          const nextVerification = (values?.verificationStatus ?? '').toString().trim().toLowerCase();
          const abatementRaw = (values?.abatementDateTime ?? '').toString().trim();
          if (!nextClinical || !nextVerification) return of(null);

          if (nextClinical === 'resolved' && !abatementRaw) {
            this.errorService.openandCloseError('Abatement Date/Time is required when clinical status is resolved.');
            return of(null);
          }

          const abatementIso = abatementRaw ? new Date(abatementRaw).toISOString() : undefined;

          const updated = {
            ...(condition as any),
            clinicalStatus: this.buildConditionClinicalStatus(nextClinical, clinicalOptions),
            verificationStatus: this.buildConditionVerificationStatus(nextVerification, verificationOptions),
            ...(nextClinical === 'resolved' ? { abatementDateTime: abatementIso } : {})
          };

          return this.http.put<any>(`${this.backend}/Condition/${conditionId}`, updated, {
            headers: new HttpHeaders({
              'Content-Type': 'application/fhir+json',
              'Prefer': 'return=representation'
            })
          }).pipe(
            catchError((e) => {
              console.error('Failed to update Condition', conditionId, e);
              this.errorService.openandCloseError('Failed to update diagnosis status.');
              return of(null);
            })
          );
        })
      ).subscribe((saved: any) => {
        if (!saved) return;
        this.state.persistPatientResource(saved as any, 'saved');
        // The check sheet observes state, so it should auto-refresh.
      });
    });
  }

  private toUniqueCodes(rawOptions: any): string[] {
    const list: any[] = Array.isArray(rawOptions) ? rawOptions : [];
    const codes = list.map((opt) => {
      if (!opt) return '';
      if (typeof opt === 'string') {
        if (opt.includes('$#$')) return (opt.split('$#$')[0] ?? '').toString().trim().toLowerCase();
        return opt.toString().trim().toLowerCase();
      }
      if (typeof opt === 'object' && typeof opt.code === 'string') {
        return opt.code.trim().toLowerCase();
      }
      if (typeof opt === 'object' && typeof opt.coding?.[0]?.code === 'string') {
        return opt.coding[0].code.trim().toLowerCase();
      }
      return '';
    }).filter(Boolean);
    return Array.from(new Set(codes));
  }

  private buildConditionClinicalStatus(code: string, rawOptions: any): any {
    const coding = this.findCodingForCode(code, rawOptions) || {
      system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
      code,
      display: code
    };
    return {
      coding: [coding],
      text: coding.display || coding.code
    };
  }

  private buildConditionVerificationStatus(code: string, rawOptions: any): any {
    const coding = this.findCodingForCode(code, rawOptions) || {
      system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
      code,
      display: code
    };
    return {
      coding: [coding],
      text: coding.display || coding.code
    };
  }

  private findCodingForCode(code: string, rawOptions: any): { system: string; code: string; display: string } | null {
    const list: any[] = Array.isArray(rawOptions) ? rawOptions : [];
    for (const opt of list) {
      if (!opt) continue;
      if (typeof opt === 'string' && opt.includes('$#$')) {
        const [c = '', display = '', system = ''] = opt.split('$#$');
        if ((c ?? '').toString().trim().toLowerCase() === code.toLowerCase()) {
          return { code: c, display: display || c, system: system || (this.backend as any) };
        }
      }
      if (typeof opt === 'object' && typeof opt.code === 'string') {
        const c = opt.code;
        if (c.trim().toLowerCase() === code.toLowerCase()) {
          return { code: c, display: opt.display || c, system: opt.system || (this.backend as any) };
        }
      }
    }
    return null;
  }

  // ----- VACCINATIONS (History) -----
  onPastVaccinationsPanelOpened(): void {
    if (this.pastVaccinationsActivated) return;
    this.pastVaccinationsActivated = true;

    const subjectToPass = this.passedEncounter ? `${this.passedEncounter['patientId']}` : this.state.currentPatientId$.getValue();

    this.checkSheetService.connectImmunizationHistory$(subjectToPass || '', null).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((imms) => {
      this.rebuildImmunizationHistoryFromImmunizations(imms || []);
    });
  }

  private rebuildImmunizationHistoryFromImmunizations(imms: Immunization[]): void {
    this.immunizationRows = (imms ?? []).map(imm => {
      const vaccine = this.utilityService.chooseFirstStringFromCodeableConcept(imm.vaccineCode) || 'Vaccine';
      const occurrence = imm.occurrenceDateTime || imm.occurrenceString || '';
      const status = imm.status || 'unknown';
      return {
        id: imm.id || '',
        vaccine,
        occurrence,
        status,
        original: imm
      };
    });
    this.immunizationTableDataSource.data = this.immunizationRows;
    this.cdr.markForCheck();
  }

  private mapVitals(obs: any[]): any {
    const out: any = {};
    const getCode = (o: any) => o?.code?.coding?.[0]?.code || o?.code?.coding?.find((c: any) => c.system === 'http://loinc.org')?.code;
    for (const o of obs || []) {
      const catCodes = (o.category || []).flatMap((c: any) => (c.coding || []).map((x: any) => x.code));
      const isVital = catCodes.includes('vital-signs');
      if (!isVital) continue;
      const code = getCode(o);
      switch (code) {
        case '8310-5': out.temperature = o.valueQuantity?.value; break;
        case '8867-4': out.pulseRate = o.valueQuantity?.value; break;
        case '9279-1': out.respiratoryRate = o.valueQuantity?.value; break;
        case '59408-5': out.oxygenSaturation = o.valueQuantity?.value; break;
        case '8302-2': out.height = o.valueQuantity?.value; break;
        case '29463-7': out.weight = o.valueQuantity?.value; break;
        case '39156-5': out.bmi = o.valueQuantity?.value; break;
        case '85354-9': {
          const sys = o.component?.find((c: any) => getCode(c) === '8480-6')?.valueQuantity?.value;
          const dia = o.component?.find((c: any) => getCode(c) === '8462-4')?.valueQuantity?.value;
          if (sys && dia) out.bloodPressure = `${sys}/${dia}`;
          break;
        }
      }
    }
    return out;
  }

  private rebuildVitalsFromVitalObservations(vitalObservations: Observation[]): void {
    this.vitalAllForCurrentEncounter = (vitalObservations ?? []).slice();

    this.vitalRows = (this.vitalAllForCurrentEncounter || []).map((v: any) => {
      const name = this.utilityService.chooseFirstStringFromCodeableConcept(v?.code);
      const isBp = (name + '').toLowerCase() === 'blood pressure';
      const value = isBp
        ? (this.utilityService.convertAllValueTypesToString(v?.component?.[0]) + '/' +
          this.utilityService.convertAllValueTypesToString(v?.component?.[1])).replace(/\s+mmHg/g, '') + ' mmHg'
        : this.utilityService.convertAllValueTypesToString(v);
      return {
        id: v.id,
        display: name,
        value,
        status: v?.status || 'unknown',
        original: v
      };
    });

    this.vitalsByCode = this.groupVitalsByCode(this.vitalRows);
    this.vitalTableDataSource.data = this.vitalsByCode.slice();
    this.cdr.markForCheck();
  }

  private rebuildPhysicalExamFromExamObservations(examObservations: Observation[]): void {
    this.examRows = (examObservations ?? []).map((o: any) => {
      const name = o.code?.text || o.code?.coding?.[0]?.display || 'Exam finding';
      let value = this.utilityService.convertAllValueTypesToString(o);
      if (!value) {
        if (o.valueString) value = o.valueString;
        else if (o.valueCodeableConcept) value = this.utilityService.chooseFirstStringFromCodeableConcept(o.valueCodeableConcept);
        else if (o.valueQuantity) value = `${o.valueQuantity.value} ${o.valueQuantity.unit || o.valueQuantity.code || ''}`.trim();
        else value = '';
      }
      return {
        id: o.id,
        name,
        value,
        status: o?.status || 'unknown',
        original: o
      };
    });

    this.examTableDataSource.data = this.examRows.slice();
    this.cdr.markForCheck();
  }

  private mapExam(obs: any[]): Array<{ name: string; value: string }> {
    const rows: Array<{ name: string; value: string }> = [];
    for (const o of obs || []) {
      const catCodes = (o.category || []).flatMap((c: any) => (c.coding || []).map((x: any) => x.code));
      const isExam = catCodes.includes('exam');
      if (!isExam) continue;
      const name = o.code?.text || o.code?.coding?.[0]?.display || 'Exam finding';
      let value = '';
      if (o.valueString) value = o.valueString;
      else if (o.valueCodeableConcept) value = o.valueCodeableConcept?.text || o.valueCodeableConcept?.coding?.[0]?.display || '';
      else if (o.valueQuantity) value = `${o.valueQuantity?.value} ${o.valueQuantity?.unit || o.valueQuantity?.code || ''}`.trim();
      rows.push({ name, value });
    }
    return rows;
  }

  private mapSymptoms(conditions: any[]): Array<any> {
    const list: any[] = [];
    for (const c of conditions || []) {
      const coding = c.code?.coding?.[0] || {};
      const clinical = c.clinicalStatus?.coding?.[0]?.code || c.clinicalStatus?.text || '';
      const verification = c.verificationStatus?.coding?.[0]?.code || c.verificationStatus?.text || '';
      const severity = c.severity?.coding?.[0]?.display || c.severity?.text || '';
      const onset = c.onsetDateTime || '';
      list.push({
        clinicalStatus: clinical,
        verificationStatus: verification,
        severity,
        onsetDateTime: onset,
        symptom: { code: coding.code, system: coding.system, display: coding.display || c.code?.text }
      });
    }
    return list;
  }
  //implement the addmoreexam functinion












  //utility se
  vitalMapForTemplate: any = [];
  private pickLatestPerCode(observations: any[]): any[] {
    const byCode = new Map<string, any>();
    const getCode = (obs: any): string | undefined =>
      obs?.code?.coding?.find((c: any) => (c?.system || '').includes('loinc'))?.code ||
      obs?.code?.coding?.[0]?.code;
    const getWhen = (obs: any): number =>
      new Date(obs?.effectiveDateTime || obs?.issued || obs?.meta?.lastUpdated || 0).getTime();

    for (const obs of observations || []) {
      const code = getCode(obs);
      if (!code) continue;
      const prev = byCode.get(code);
      if (!prev || getWhen(obs) > getWhen(prev)) {
        byCode.set(code, obs);
      }
    }
    return Array.from(byCode.values());
  }
  vitalAllForCurrentEncounter: any = [];
  vitalsByCode: Array<[any, any[]]> = [];
  vitalTableDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  displayedColumns: string[] = ['display', 'value', 'status', 'actions', 'history'];
  examDisplayedColumns: string[] = ['name', 'value', 'status', 'actions'];
  examTableDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  // Complaints table state
  complaintDisplayedColumns: string[] = ['symptom', 'severity', 'onset', 'actions'];
  complaintTableDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  complaintRows: Array<{ id: string; symptom: string; severity: string; verification: string; onset: string; original: any }> = [];
  vitalRows: Array<{ id: string; display: string; value: string; status: string; original: any }> = [];
  examRows: Array<{ id: string; name: string; value: string; status: string; original: any }> = [];
  showAddVitals = false;
  @Input() passedEncounter: {
    patientId: string
    status: Encounter['status'],
    [key: string]: any
  } | null = null;
  private afterResolvedDataLoaded() {
    const currentEncounterId = this.passedEncounter?.['id'] || this.state.currentEncounter.getValue()?.['id'] || null;

    // --- MEDICATION REQUESTS (Filtered by Encounter) ---
    const allMedicationRequests = this.sourceData?.medicationRequests || [];
    const medRequests = allMedicationRequests.filter((m: any) => {
      if (!currentEncounterId) return true;
      return m.encounter?.reference === `Encounter/${currentEncounterId}` ||
        m.encounter?.reference?.endsWith(`/${currentEncounterId}`);
    });

    this.medRequestAndValue = medRequests.map((m: any) => {
      const name = m.medicationCodeableConcept?.text || m.medicationCodeableConcept?.coding?.[0]?.display || 'Unknown Medication';
      const value = m.dosageInstruction?.[0]?.text || 'No dosage info';
      return { name, value, original: m };
    });

    // --- LAB OBSERVATIONS ---
    const allObs: any[] = Array.isArray(this.sourceData?.observations) ? this.sourceData.observations : [];

    // Filter for laboratory observations
    const labObs = allObs.filter((o: any) => {
      return this.utilityService.searchCodeableConceptByDisplayOrText(o?.category?.[0], 'laboratory');
    });

    this.labRequestAndValue = labObs.map((o: any) => {
      const name = o.code?.text || o.code?.coding?.[0]?.display || 'Lab Test';
      let value = this.utilityService.convertAllValueTypesToString(o);
      if (!value) {
        if (o.valueString) value = o.valueString;
        else if (o.valueCodeableConcept)
          value = this.utilityService.chooseFirstStringFromCodeableConcept(o.valueCodeableConcept);
        else if (o.valueQuantity)
          value = `${o.valueQuantity.value} ${o.valueQuantity.unit || o.valueQuantity.code || ''}`.trim();
        else value = '';
      }
      return { name, value, original: o };
    });

    // Compute vitals snapshot from observations for the latest encounter only
    this.vitalAllForCurrentEncounter = currentEncounterId
      ? this.getVitalObservationRelatedToLastEncounter(`Encounter/${currentEncounterId}`)
      : [];

    // Use only the latest-final observation from each group for the main table
    this.vitalRows = (this.vitalAllForCurrentEncounter || []).map((v: any) => {
      const name = this.utilityService.chooseFirstStringFromCodeableConcept(v?.code);
      const isBp = (name + '').toLowerCase() === 'blood pressure';
      const value = isBp
        ? (this.utilityService.convertAllValueTypesToString(v?.component?.[0]) + '/' +
          this.utilityService.convertAllValueTypesToString(v?.component?.[1])).replace(/\s+mmHg/g, '') + ' mmHg'
        : this.utilityService.convertAllValueTypesToString(v);
      return {
        id: v.id,
        display: name,
        value,
        status: v?.status || 'unknown',
        original: v
      };
    });

    this.vitalsByCode = this.groupVitalsByCode(this.vitalRows);
    this.vitalTableDataSource.data = this.vitalsByCode.slice();
    this.cdr.markForCheck();

    // Build exam observations (category 'exam') for current encounter
    const examObsForEncounter = allObs.filter(o => {
      const isPatientPerformer = o.performer?.some((p: any) => p.reference?.toLowerCase().includes('patient/'));
      return o?.encounter?.reference === `Encounter/${currentEncounterId}` &&
        this.utilityService.searchCodeableConceptByDisplayOrText(o?.category?.[0], 'Exam') && !isPatientPerformer
    });

    this.examRows = examObsForEncounter.map((o: any) => {
      const name = o.code?.text || o.code?.coding?.[0]?.display || 'Exam finding';
      // Reuse existing utility to stringify value; fallback to known value fields
      let value = this.utilityService.convertAllValueTypesToString(o);
      if (!value) {
        if (o.valueString) value = o.valueString;
        else if (o.valueCodeableConcept)
          value = this.utilityService.chooseFirstStringFromCodeableConcept(o.valueCodeableConcept);
        else if (o.valueQuantity)
          value = `${o.valueQuantity.value} ${o.valueQuantity.unit || o.valueQuantity.code || ''}`.trim();
        else value = '';
      }
      return {
        id: o.id,
        name,
        value,
        status: o?.status || 'unknown',
        original: o
      };
    });

    this.examTableDataSource.data = this.examRows.slice();

    // Ensure table updates on every emission (new reference) and trigger CD
    this.examTableDataSource.data = this.examRows.slice();
    this.cdr.markForCheck();

    // Prepare verified conditions datasource for the Condition card
    const conditionsAll: any[] = Array.isArray(this.sourceData?.condition) ? this.sourceData.condition : [];
    // this.conditionDataSource = conditionsAll;
    this.patientConditionDataSource = conditionsAll.filter((condition: Condition) => {
      // asserter is a practitioner
      if (!this.passedEncounter) {
        // if (condition.encounter?.reference === `Encounter/${this.state.currentEncounter.getValue()?.['id']}`) {
// $#$        //   /* alert(JSON.stringify(condition) */);
        // }
        return condition.asserter && condition.asserter.reference && condition.asserter.reference.toLowerCase().startsWith('patient/')
          && condition.encounter?.reference === `Encounter/${this.state.currentEncounter.getValue()?.['id']}`
      } else {
        return condition.asserter && condition.asserter.reference && !condition.asserter.reference.toLowerCase().startsWith('patient/')
          && condition.encounter?.reference === `Encounter/${this.passedEncounter?.['id']}`
      };
    });

    this.practitionerConditionDataSource = conditionsAll.filter((condition: Condition) => {
      // asserter is a practitioner
      if (!this.passedEncounter) {
        return condition.asserter && condition.asserter.reference && condition.asserter.reference.toLowerCase().startsWith('patient/')
          && condition.encounter?.reference === `Encounter/${this.state.currentEncounter.getValue()?.['id']}`
      } else {
        return condition.asserter && condition.asserter.reference && condition.asserter.reference.toLowerCase().startsWith('patient/')
          && condition.encounter?.reference === `Encounter/${this.passedEncounter?.['id']}`
      };
    });

    // Build complaints rows from patientConditionDataSource (chief complaints asserted by patient)
    this.complaintRows = (this.patientConditionDataSource || []).map((c: any) => {
      const symptom = c?.code?.coding?.[0]?.display || c?.code?.text || c?.code?.coding?.[0]?.code || '';
      const severity = c?.severity?.coding?.[0]?.display || c?.severity?.text || '';
      const verification = c?.verificationStatus?.coding?.[0]?.display
        || c?.verificationStatus?.coding?.[0]?.code
        || c?.verificationStatus?.text
        || '';
      let onset = c?.onsetDateTime || '';
      // convert onset to duration = onset - now if it's still actieve

      const clinicalStatus = c?.clinicalStatus?.coding?.[0]?.display || c?.clinicalStatus?.text || '';
      return {
        id: c.id,
        symptom,
        severity,
        verification,
        onset,
        original: c
      };
    });
    this.complaintTableDataSource.data = this.complaintRows.slice();
    this.cdr.markForCheck();
  }

  toggleVitalStatus(row: { status: string }) {
    row.status = row.status === 'active' ? 'inactive' : 'active';
  }

  // Persist a FHIR transaction bundle to the backend
  private processBundle(bundle: any) {
    return this.http.post<any>(`${this.backend}`, bundle, {
      headers: new HttpHeaders({
        'Content-Type': 'application/fhir+json',
        'Prefer': 'return=representation'
      })
    }).pipe(tap(e => this.state.processBundleTransaction(e)));
  }

  // Build a transaction bundle that updates the Observation status via PUT
  private buildObservationStatusBundle(updatedObservation: any) {
    return {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: [
        {
          resource: updatedObservation,
          request: {
            method: 'PUT',
            url: `Observation/${updatedObservation.id}`
          }
        }
      ]
    };
  }

  // change status on the underlying Observation resource and persist
  changeVitalStatus(row: { status: string; original: any }, newStatus: 'entered-in-error' | 'cancelled') {
    if (!row?.original?.id) return;

    const prevStatus = row.status;

    // Optimistic update
    // row.original.status = newStatus;
    // row.status = newStatus;

    // Clone for payload to avoid accidental reference issues
    const updated = JSON.parse(JSON.stringify({ ...row.original, status: newStatus }));
    const bundle = this.buildObservationStatusBundle(updated);

    this.processBundle(bundle).subscribe({
      next: () => {
        // Refresh table datasource
        // this.vitalTableDataSource.data = this.vitalRows.slice();
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to persist status change', err);
        // Revert on failure
        // row.original.status = prevStatus;
        // row.status = prevStatus;
        // this.vitalTableDataSource.data = this.vitalRows.slice();
        this.cdr.markForCheck();
      }
    });
  }

  changeExamStatus(row: { status: string; original: any }, newStatus: 'entered-in-error' | 'cancelled') {
    if (!row?.original?.id) return;
    const prev = row.status;
    // row.original.status = newStatus;
    // row.status = newStatus;
    const updated = JSON.parse(JSON.stringify({ ...row.original, status: newStatus }));
    const bundle = this.buildObservationStatusBundle(updated);
    this.processBundle(bundle).subscribe({
      next: () => {
        // this.examTableDataSource.data = this.examRows.slice();
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to persist exam status change', err);
        // row.original.status = prev;
        // row.status = prev;
        // this.examTableDataSource.data = this.examRows.slice();
        this.cdr.markForCheck();
      }
    });
  }

  // Build a transaction bundle that updates a Condition verificationStatus via PUT
  private buildConditionStatusBundle(updatedCondition: any) {
    return {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: [
        {
          resource: updatedCondition,
          request: {
            method: 'PUT',
            url: `Condition/${updatedCondition.id}`
          }
        }
      ]
    };
  }

  // Change verificationStatus on the Condition (chief complaint) and persist
  changeComplaintStatus(
    row: { verification: string; original: any },
    newVerification: 'entered-in-error' | 'refuted'
  ) {
    if (!row?.original?.id) return;
    const prev = row.original?.verificationStatus;

    // Optimistic update to verificationStatus
    row.original.verificationStatus = {
      coding: [
        {
          system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
          code: newVerification,
          display: newVerification.replace(/-/g, ' ')
        }
      ],
      text: newVerification.replace(/-/g, ' ')
    };
    row.verification = row.original.verificationStatus.text;

    const updated = JSON.parse(JSON.stringify(row.original));
    const bundle = this.buildConditionStatusBundle(updated);

    this.processBundle(bundle).subscribe({
      next: () => {
        this.complaintTableDataSource.data = this.complaintRows.slice();
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to persist complaint status change', err);
        // Revert on failure
        row.original.verificationStatus = prev;
        row.verification =
          prev?.coding?.[0]?.display || prev?.coding?.[0]?.code || prev?.text || row.verification;
        this.complaintTableDataSource.data = this.complaintRows.slice();
        this.cdr.markForCheck();
      }
    });
  }

  submitVitals() {
    const active = this.vitalRows.filter(r => r.status === 'active').map(r => r.original);
    console.log('Submitting vitals:', active);
    // TODO: implement submission as needed
  }
  addMoreVitals() {
    this.showAddVitals = true;
    this.dialog.open(AddVitalsComponent, {
      maxHeight: "90vh",
      maxWidth: "680px"
    });
  }

  // implement the addMoreExam function
  addMoreExam() {
    const ref = this.dialog.open(AddObservationComponent, {
      maxHeight: '90vh',
      maxWidth: '1200px',
      autoFocus: false,
      data: {
        isAnyCategory: false,
        observationCategoryValue: "exam"
      }
    })
    ref.afterClosed().subscribe(() => {
      // Refresh tables in case state stream hasn't emitted yet
      // this.afterResolvedDataLoaded();
    });
  }

  private getVitalObservationRelatedToLastEncounter(lastEncounterReference: string): any[] {
    //lastencounterReference string regex check
    if (lastEncounterReference && lastEncounterReference.match(/^Encounter\/[A-Za-z0-9\-\.]{1,64}$/)) {
// $#$      // /* alert(lastEncounterReference); */
      //get Obervations that have encouter reference as this and ha category
      const observationsAll: any[] = Array.isArray(this.sourceData?.observations) ? this.sourceData.observations : [];
// $#$      // /* alert(JSON.stringify(observationsAll) */)
      const filteredObservations = observationsAll.filter(obs => {


        const encounterRef = obs?.encounter?.reference || '';
// $#$        // /* alert(".............."); */ /* alert(encounterRef === lastEncounterReference && this.utilityService.searchCodeableConceptByDisplayOrText(obs?.category?.[0], 'vital signs') */);
        return encounterRef === lastEncounterReference && this.utilityService.searchCodeableConceptByDisplayOrText(obs?.category?.[0], 'vital signs');
      });
      // filteredObservations.forEach(
// $#$      //   (e) => { /* alert(JSON.stringify(e) */) }
      // )
// $#$      // /* alert(JSON.stringify(filteredObservations) */);
      return filteredObservations;

    } else {
      return [];
    }


  }

  /**
   * Group vitals by LOINC code. For each code, return [latestFinalObs, [otherObs...]].
   * latestFinalObs = most recent observation with status='final', sorted by effectiveDateTime desc.
   * otherObs = all remaining observations for that code, sorted by effectiveDateTime desc.
   */
  private groupVitalsByCode(observations: any[]): Array<[any, any[]]> {
// $#$    // /* alert(JSON.stringify(observations) */);
    // observations = observations.map(o => o.original);
    const byCode = new Map<string, any[]>();
    const getCode = (obs: any) =>
      obs?.original?.code?.coding?.[0]?.code
    const getTime = (obs: any) =>
      new Date(obs?.original?.effectiveDateTime || obs?.original?.issued || obs?.original?.meta?.lastUpdated || 0).getTime();

    for (const obs of observations || []) {
      const code = getCode(obs);
      if (!code) continue;
      if (!byCode.has(code)) byCode.set(code, []);
      byCode.get(code)!.push(obs);
    }

    const result: Array<[any, any[]]> = [];
    for (const [code, list] of byCode.entries()) {
      // sort all by effectiveDateTime desc
      list.sort((a, b) => getTime(b) - getTime(a));

      // find first with status='final'
      const finalIdx = list.findIndex(o => (o.status || '').toLowerCase() === 'final');
      if (finalIdx > -1) {
        const latest = list[finalIdx];
        const others = list//.filter((_, i) => i !== finalIdx);
        result.push([latest, others]);
      } else {
        // no final status, treat first as "latest", rest as others
        // const [latest, ...others] = list;
        // result.push([{
        //    id: 'N/A',
        // display: 'N/A',
        // value: 'N/A',
        // status: 'N/A',
        // original: {}
        // }, list]);
      }
    }
// $#$    // /* alert(JSON.stringify(result) */);
    return result;
  }

  showVitalHistory(row: [any, any[]]) {
    // const code = this.getLoincCode(row[0].code);
    // const group = this.vitalsByCode.find(([latest, _]) => this.getLoincCode(latest) === code);
    // if (!group) return;

    // const [_latest, others] = group;
    // const historyRows = others.map((v: any) => {
    //   const name = this.utilityService.chooseFirstStringFromCodeableConcept(v?.code);
    //   const isBp = (name + '').toLowerCase() === 'blood pressure';
    //   const value = isBp
    //     ? (this.utilityService.convertAllValueTypesToString(v?.component?.[0]) + '/' +
    //       this.utilityService.convertAllValueTypesToString(v?.component?.[1])).replace(/\s+mmHg/g, '') + ' mmHg'
    //     : this.utilityService.convertAllValueTypesToString(v);
    //   return {
    //     id: v.id,
    //     display: name,
    //     value,
    //     status: v?.status || 'unknown',
    //     effectiveDateTime: v?.effectiveDateTime || v?.issued || '',
    //     original: v
    //   };
    // });

    this.dialog.open(VitalHistoryDialogComponent, {
      maxWidth: '800px',
      maxHeight: '90vh',
      data: { vitalName: this.utilityService.chooseFirstStringFromCodeableConcept(row[0]?.original.code), history: row[1] }
    });
  }

  private getLoincCode(obs: any): string | undefined {
    return obs?.code?.coding?.find((c: any) => c.system?.includes('loinc'))?.code ||
      obs?.code?.coding?.[0]?.code;
  }
}
// NOTE: Observation display sources:
// - Vitals table: built here (vitalTableDataSource) from Observation category 'vital signs'.
// - Exam table: built here (examTableDataSource) from Observation category 'Exam'.
// - Chief complaints table: built here from Condition resources.
// - AddObservationComponent: used to create new Observation entries (opened in addMoreExam()).
// - Shared table renderer likely in ../shared/table-interface (imported via commonImports).
